import Clear from '@mui/icons-material/Clear';
import Error from '@mui/icons-material/Error';
import Save from '@mui/icons-material/Save';
import UploadFile from '@mui/icons-material/UploadFile';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import useTheme from '@mui/material/styles/useTheme';
import { type AxiosProgressEvent } from 'axios';
import { lookup } from 'mime-types';
import { type CSSProperties, type ReactEventHandler, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactCrop, { type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Iconify from '../../components/iconify/component';
import { type IconifyProps } from '../../components/iconify/types';
import { type CustomTheme } from '../../provider/ThemeProvider';
import { uploadFile as uploadFileAxios } from '../AxiosVars';
import { formatTimestamp } from '../Date';
import ImageEl, { type ImageProps } from '../Image';
import Image360a from '../Image360a';
import Image360i from '../Image360i';
import { getSessionHeader, getToken } from '../Session';
import Toast from '../Toast';
import { type FileTypeAccept, checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull } from '../Tools';
import { getFileComponent } from './file-component';

const TO_RADIANS = Math.PI / 180;

const styles = {
    btnOverlay: {
        'zIndex': 20,
        'transition': 'all 0.2s ease-in-out',
        'position': 'absolute',
        'top': 0,
        'width': '100%',
        'height': '100%',
        'opacity': 0,
        'textAlign': 'center',
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'center',
        '&:hover': {
            background: 'rgba(0, 0, 0, 0.7)',
            opacity: 1,
        },
    },
    overlaySuccess: {
        zIndex: 20,
        transition: 'all 0.2s ease-in-out',
        position: 'absolute',
        top: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(76, 175, 80, 0.7)',
        opacity: 1,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    overlayError: {
        zIndex: 20,
        transition: 'all 0.2s ease-in-out',
        position: 'absolute',
        top: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(244, 67, 54, 0.7)',
        opacity: 1,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
};

export type UploadConfig = {
    onUploadProgress: (progressEvent: Pick<AxiosProgressEvent, 'loaded' | 'total'>) => void;
    headers: { [index: string]: string };
};

export type UploadArrayItem = { error?: string; file: File; progress: number | boolean; url: string };

export type UploadObject = true | { array: UploadArrayItem[]; lastUpdate: number } | undefined;

export type InputImageProps = {
    aspectRatio?: ImageProps['ratio'];
    autoWidth?: boolean;
    blink?: boolean;
    callback?: (params: { id: string; val: File[] }) => void;
    callbackUpload?: (id: string, file: File, config: UploadConfig, nr: number) => Promise<void>;
    className?: string;
    cover?: boolean;
    cropable?: boolean;
    disabled?: boolean;
    edit?: boolean;
    error?: string;
    externalImage?: boolean;
    fileSizeKb?: number;
    fileSizePixel?: { minWidth?: number; maxWidth?: number; minHeight?: number; maxHeight?: number };
    fileType?: FileTypeAccept;
    height?: string;
    hideFullscreen?: boolean;
    id?: string;
    loading?: boolean;
    max?: number;
    multiple?: boolean;
    onClick?: () => void;
    onLoad?: ReactEventHandler<HTMLImageElement>;
    overlay?: {
        disabled?: boolean;
        func?: () => void;
        hidden?: boolean;
        icon?: IconifyProps;
        style?: CSSProperties;
        textIcon?: string | number;
        title?: string;
    }[];
    overlayWithoutValue?: boolean;
    round?: boolean;
    step?: number;
    testid?: string;
    type?: 'avatar' | 'image' | 'image360i' | 'image360a';
    uploadId?: number;
    v?: string;
    vNew?: string;
};

export function convertImageValue(v: InputImageProps['v'], loading: InputImageProps['loading']): string {
    const val = loading ? '' : !checkUndefinedNullOrEmpty(v) ? v : null;
    return val;
}

function InputImage(moduleProps: InputImageProps) {
    const {
        aspectRatio,
        autoWidth,
        blink,
        callback,
        callbackUpload,
        cover,
        cropable,
        disabled,
        edit,
        error,
        externalImage,
        fileSizeKb,
        fileSizePixel,
        fileType,
        height,
        hideFullscreen,
        id,
        loading,
        max,
        multiple,
        onClick,
        onLoad,
        overlay,
        overlayWithoutValue,
        round,
        step,
        testid,
        type,
        uploadId,
        v,
        vNew,
    }: InputImageProps = {
        aspectRatio: [4, 3],
        autoWidth: false,
        blink: false,
        cover: false,
        cropable: false,
        disabled: false,
        edit: false,
        fileSizeKb: 10240,
        fileType: {
            'application/pdf': ['.pdf'],
            'application/zip': ['.zip'],
            'image/jpg': ['.jpeg', '.jpg'],
            'image/png': ['.png'],
            'image/tiff': ['.tif', '.tiff'],
        },
        hideFullscreen: false,
        loading: false,
        multiple: false,
        overlayWithoutValue: false,
        round: false,
        type: 'image',
        uploadId: 0,
        ...moduleProps,
    };

    const [crop, setCrop] = useState<PixelCrop>(undefined);
    const [cropPreview, setCropPreview] = useState<Blob>(undefined);
    const [heightFrame, setHeightFrame] = useState<number>(undefined);
    const [preview, setPreview] = useState<string>(undefined);
    const [showClose, setShowClose] = useState<boolean>(false);
    const [uploading, setUploading] = useState<UploadObject>(undefined);
    const [value, setValue] = useState<InputImageProps['v']>(v);
    const { getRootProps, getInputProps } = useDropzone({
        accept: fileType,
        disabled: disabled || !checkUndefinedNullOrEmpty(error),
        multiple: multiple,
        noClick: !checkUndefinedOrNull(onClick),
        onDrop: (accepted) => uploadFiles(accepted, false),
        validator: () => null,
    });
    const imageRef = useRef<HTMLImageElement>(undefined);
    const theme: CustomTheme = useTheme();

    const updateUploadState = (newState: UploadArrayItem[]) => {
        if (!checkUndefined(newState)) {
            setUploading({ array: newState, lastUpdate: formatTimestamp(new Date()) });
        } else {
            setUploading(undefined);
        }
    };

    const doFileUpload = async (file: File, i: number, config: UploadConfig): Promise<{ success: true; newFile: unknown } | { success: false; error: string }> => {
        const nr = (uploadId || 0) + i + 1;
        if (!checkUndefinedOrNull(callbackUpload)) {
            await callbackUpload(id, file, config, nr);
            setPreview(undefined);
            return { success: true, newFile: undefined };
        } else {
            const formData = new FormData();
            formData.append('images', file);

            try {
                const serviceUpload = (await uploadFileAxios(formData, config))?.data;
                return { success: true, newFile: serviceUpload?.files?.[0] };
            } catch (err: any) {
                return { success: false, error: err?.response?.data };
            }
        }
    };

    const checkUploadFile = async (file: File, previewUrl: string): Promise<{ success: true } | { success: false; error: string }> => {
        if (checkUndefinedOrNull(file)) {
            return { success: false, error: 'Keine Datei gefunden' };
        }
        if (!checkUndefinedOrNull(fileType)) {
            const mimeTypes = Object.keys(fileType);
            const allowedFiletypes = [];
            const fileAccepted = mimeTypes?.some((mime) => {
                const fileTypes = fileType?.[mime];

                return fileTypes?.some((t) => {
                    allowedFiletypes.push(t);
                    return file?.name?.toLowerCase()?.includes(t);
                });
            });
            if (!fileAccepted) {
                return {
                    success: false,
                    error: `Falsches Dateiformat!
Upload: ${file?.name?.substring(file?.name?.lastIndexOf('.'))} | Erlaubt: ${allowedFiletypes.join(', ')}`,
                };
            }
        }
        if (!checkUndefinedOrNull(fileSizeKb)) {
            if (file?.size > fileSizeKb * 1024) {
                return {
                    success: false,
                    error: `Uploadgröße überschritten!
Upload: ${(file?.size / 1024 / 1024).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}MB | Erlaubt: ${(fileSizeKb / 1024).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}MB`,
                };
            }
        }
        if (!checkUndefinedOrNull(fileSizePixel)) {
            const img = new Image();
            img.src = previewUrl;
            await img.decode();
            if (img?.width < fileSizePixel?.minWidth || img?.width > fileSizePixel?.maxWidth || img?.height < fileSizePixel?.minHeight || img?.height > fileSizePixel?.minHeight) {
                return {
                    success: false,
                    error: `Falsches Bildformat!
Upload: ${[img.width, img.height].join(' x ')} | Erlaubt: min: ${[fileSizePixel.minWidth, fileSizePixel.minHeight].join(' x ')}, max: ${[fileSizePixel.maxWidth, fileSizePixel.maxHeight].join(' x ')}`,
                };
            }
        }

        return { success: true };
    };

    const uploadFiles = async (files: File[], forceUpload: boolean): Promise<void> => {
        const stateArray: UploadArrayItem[] = [];
        if (checkUndefinedNullOrEmpty(files)) {
            updateUploadState(undefined);
            Toast.addError({ key: 'InputField_upload', primary: 'Keine Dateien zum Upload vorhanden!' });
            console.error('Keine Dateien zum Upload vorhanden!');
            return;
        }

        // Setup all files
        for (let i = 0; i < files?.length; i++) {
            // Create status object
            stateArray[i] = { file: files?.[i], url: URL.createObjectURL(files?.[i]), progress: 0 };
            updateUploadState(stateArray);
        }

        for (let i = 0; i < files?.length; i++) {
            // Check file
            const fileCheck = await checkUploadFile(stateArray?.[i]?.file, stateArray?.[i]?.url);
            if (fileCheck.success === false) {
                stateArray[i] = { ...stateArray[i], progress: false, error: fileCheck?.error };
                updateUploadState(stateArray);
                continue;
            }

            // Jump out if croppable
            if (cropable) {
                setPreview(stateArray?.[i]?.url);
                if (!forceUpload) {
                    return;
                }
            }

            // Do file upload
            const fileUploadConfig: UploadConfig = {
                headers: { [getSessionHeader() || 'authentication']: 'Bearer ' + getToken(), 'Content-Type': lookup(stateArray?.[i]?.file?.name) || 'application/x-www-form-urlencoded' },
                onUploadProgress: (progressEvent) => {
                    if (stateArray[i].progress !== true && stateArray[i].progress !== false) {
                        stateArray[i] = {
                            ...stateArray[i],
                            progress:
                                progressEvent?.loaded < 0 && progressEvent?.total < 0
                                    ? false
                                    : progressEvent.loaded === progressEvent.total
                                      ? true
                                      : Math.round((progressEvent.loaded * 100) / progressEvent.total),
                        };
                        updateUploadState(stateArray);
                    }
                },
            };
            const fileUpload = await doFileUpload(stateArray?.[i]?.file, i, fileUploadConfig);
            if (fileUpload.success === false) {
                stateArray[i] = { ...stateArray[i], progress: false, error: fileUpload?.error };
                updateUploadState(stateArray);
                continue;
            } else {
                stateArray[i] = { ...stateArray[i], progress: true };
                updateUploadState(stateArray);
            }
        }

        const errorCount = stateArray?.filter((state) => state?.progress === false)?.length;

        const filesForCallback = value || [];
        if (Array.isArray(filesForCallback) && callback) {
            stateArray.map((file) => {
                filesForCallback.push(file.file);
            });
        }
        if (Array.isArray(filesForCallback) && callback) {
            callback({ id: id, val: filesForCallback });
        }

        if (errorCount > 0) {
            if (errorCount === stateArray?.length) {
                Toast.addError({ key: 'InputField_upload', primary: 'Upload fehlgeschlagen' });
                setShowClose(true);
                return;
            } else {
                Toast.addWarning({ key: 'InputField_upload', primary: 'Upload mit Fehlern abgeschlossen' });
                setShowClose(true);
                return;
            }
        } else {
            Toast.addSuccess({ key: 'InputField_upload', primary: 'Upload abgeschlossen' });
            updateUploadState(undefined);
            return;
        }
    };

    function getCroppedImg(image: HTMLImageElement, c: PixelCrop, scale = 1, rotate = 0): Promise<Blob> {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            return;
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        // devicePixelRatio slightly increases sharpness on retina devices
        // at the expense of slightly slower render times and needing to
        // size the image back down if you want to download/upload and be
        // true to the images natural size.
        const pixelRatio = window.devicePixelRatio;
        // const pixelRatio = 1

        canvas.width = Math.floor(c.width * scaleX * pixelRatio);
        canvas.height = Math.floor(c.height * scaleY * pixelRatio);

        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingQuality = 'high';

        const cropX = c.x * scaleX;
        const cropY = c.y * scaleY;

        const rotateRads = rotate * TO_RADIANS;
        const centerX = image.naturalWidth / 2;
        const centerY = image.naturalHeight / 2;

        ctx.save();

        // 5) Move the crop origin to the canvas origin (0,0)
        ctx.translate(-cropX, -cropY);
        // 4) Move the origin to the center of the original position
        ctx.translate(centerX, centerY);
        // 3) Rotate around the origin
        ctx.rotate(rotateRads);
        // 2) Scale the image
        ctx.scale(scale, scale);
        // 1) Move the center of the image to the origin (0,0)
        ctx.translate(-centerX, -centerY);
        ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight);

        ctx.restore();

        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        // reject(new Error('Canvas is empty'));
                        console.error('Canvas is empty');
                        return;
                    }
                    resolve(blob);
                },
                'image/jpg',
                70
            );
        });
    }

    const makeClientCrop = async (c) => {
        if (!checkUndefinedOrNull(imageRef) && !checkUndefinedNullOrEmpty(c)) {
            const croppedImageUrl = await getCroppedImg(imageRef.current, c);
            setCropPreview(croppedImageUrl);
        }
    };

    useEffect(
        function () {
            setValue(!checkUndefined(vNew) ? vNew : v);
        },
        [v, vNew]
    );

    useEffect(
        function () {
            if (blink) {
                setTimeout(() => {
                    const element = document.getElementById(id);
                    if (!checkUndefinedOrNull(element)) {
                        element.scrollIntoView({ block: 'center' });
                    }
                }, 500);
            }
        },
        [blink, id]
    );

    const showOverlay = (overlayWithoutValue || (!overlayWithoutValue && convertImageValue(value, loading))) && !loading && !checkUndefinedNullOrEmpty((overlay || []).filter((a) => !a.hidden));
    const elementSphere360 =
        type === 'image360i' ? (
            !loading && !checkUndefinedNullOrEmpty(convertImageValue(value, loading)) ? (
                <Image360i
                    key="lightbox_innen"
                    id={'360iComponent_' + id}
                    ratio={aspectRatio}
                    src={convertImageValue(value, loading)}
                    config={{ showControls: !showOverlay, showFullscreenCtrl: !hideFullscreen }}
                />
            ) : (
                <ImageEl externalImage onLoad={onLoad} ratio={aspectRatio} />
            )
        ) : undefined;
    const elementSprite360 =
        type === 'image360a' ? (
            <>
                {checkUndefinedNullOrEmpty(heightFrame) && (
                    <img
                        key="previewImage"
                        alt=""
                        id={'getImageHeight_' + id}
                        style={{ display: 'none' }}
                        src={convertImageValue(value, loading)}
                        onLoad={() => {
                            const fullHeight = (document.getElementById('getImageHeight_' + id) as HTMLImageElement).naturalHeight;
                            setHeightFrame(fullHeight / (step ? step : 24));
                        }}
                    />
                )}
                {!loading && !checkUndefinedNullOrEmpty(heightFrame) && !checkUndefinedNullOrEmpty(convertImageValue(value, loading)) ? (
                    <Image360a key="image_360aussen" width={max || 640} height={heightFrame || 480} step={step || 24} src={convertImageValue(value, loading)} />
                ) : (
                    <ImageEl externalImage onLoad={onLoad} ratio={[max || 640, heightFrame || 480]} />
                )}
            </>
        ) : undefined;

    const rootProps = edit && getRootProps();
    const inputProps = edit && getInputProps();

    return edit ? (
        <div
            className={[blink ? 'blinkInputfield' : '', 'inputImage'].join(' ')}
            style={{ background: theme.palette.background.default, position: 'relative', height: height || '100%', width: !autoWidth ? '100%' : undefined }}
            key={'inputImage_' + id}
        >
            <Box {...rootProps} onClick={!checkUndefinedOrNull(onClick) ? onClick : rootProps.onClick} sx={{ height: '100%' }}>
                <input id={id} {...inputProps} />
                <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20 }}>
                    {checkUndefinedNullOrEmpty(error) && !showOverlay && checkUndefinedNullOrEmpty(convertImageValue(value, loading)) && edit && (
                        <Tooltip title="Bild hochladen">
                            <UploadFile sx={{ fill: theme.palette.text.primary }} />
                        </Tooltip>
                    )}
                    {!checkUndefinedNullOrEmpty(error) && (
                        <Tooltip title={error}>
                            <Error sx={{ fill: theme.palette.text.primary }} />
                        </Tooltip>
                    )}
                </Stack>
                {type === 'image360i' ? (
                    elementSphere360
                ) : type === 'image360a' ? (
                    elementSprite360
                ) : type === 'avatar' ? (
                    <Avatar src={convertImageValue(value, loading) || 'undefined'} style={{ width: height, height: height }} />
                ) : (
                    <ImageEl key="imageElement" contain={!cover} externalImage={externalImage} onLoad={onLoad} ratio={aspectRatio} src={convertImageValue(value, loading)} />
                )}
            </Box>
            {checkUndefinedNullOrEmpty(error) && showOverlay && (
                <Box sx={styles.btnOverlay}>
                    {overlay.map(
                        (btn) =>
                            btn &&
                            !btn.hidden && (
                                <Tooltip key={'inputImage' + id + '_overlay_' + btn.title} title={btn.title}>
                                    <span>
                                        <IconButton
                                            aria-label={btn.title}
                                            disabled={btn.disabled}
                                            onClick={btn.func}
                                            style={{ color: btn.disabled ? '#aaa' : '#fff', fontSize: '2rem', ...btn.style }}
                                            size="large"
                                        >
                                            {btn.textIcon ? btn.textIcon : <Iconify icon={btn.icon} style={{ color: 'inherit' }} />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )
                    )}
                </Box>
            )}
            {type !== 'image360i' && type !== 'image360a' && cropable && preview ? (
                <Dialog key="cropDialog" open onClose={() => setPreview(undefined)}>
                    <DialogTitle>Bild zuschneiden</DialogTitle>
                    <DialogContent>
                        <ReactCrop
                            aspect={aspectRatio[0] / aspectRatio[1]}
                            circularCrop={type === 'avatar' || round}
                            keepSelection
                            ruleOfThirds
                            crop={crop}
                            onChange={(c) => setCrop(c)}
                            onComplete={makeClientCrop}
                        >
                            <img
                                ref={imageRef}
                                alt=""
                                src={preview}
                                style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 191px)' }}
                                onLoad={(img) => {
                                    if (!checkUndefinedOrNull(img?.currentTarget)) {
                                        const { width: mediaWidth, height: mediaHeight } = img.currentTarget;
                                        setCrop(
                                            centerCrop(
                                                makeAspectCrop(
                                                    {
                                                        unit: 'px',
                                                        width: mediaWidth > mediaHeight ? mediaHeight : mediaWidth,
                                                    },
                                                    aspectRatio[0] / aspectRatio[1],
                                                    mediaWidth,
                                                    mediaHeight
                                                ),
                                                mediaWidth,
                                                mediaHeight
                                            )
                                        );
                                    }
                                }}
                            />
                        </ReactCrop>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="primary"
                            onClick={() => {
                                setPreview(undefined);
                                setUploading(undefined);
                            }}
                        >
                            <Clear style={{ marginRight: 8 }} />
                            Abbrechen
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => {
                                const file = new File([cropPreview], 'image.jpeg');
                                uploadFiles([file], true);
                            }}
                        >
                            <Save style={{ marginRight: 8 }} />
                            Speichern
                        </Button>
                    </DialogActions>
                </Dialog>
            ) : undefined}
            {!checkUndefinedOrNull(uploading) && (
                <Dialog open maxWidth="md" fullWidth onClose={showClose ? () => setUploading(undefined) : undefined}>
                    <DialogTitle>Upload</DialogTitle>
                    <DialogContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {uploading === true ? undefined : (
                            <Grid container spacing={2} justifyContent="center" sx={{ overflow: 'auto' }}>
                                {uploading?.array?.map((file) => getFileComponent(file, testid || id))}
                            </Grid>
                        )}
                        <LinearProgress
                            color={uploading !== true && uploading?.array?.some((u) => u?.progress === false) ? 'error' : 'primary'}
                            value={
                                uploading === true
                                    ? 0
                                    : (uploading?.array?.map((u) => (typeof u?.progress === 'boolean' ? 100 : u?.progress))?.reduce((a, b) => a + b, 0) || 0) / uploading.array.length
                            }
                            valueBuffer={0}
                            sx={{ mt: 3, width: 1 }}
                            variant={uploading === true ? 'indeterminate' : 'buffer'}
                        />
                    </DialogContent>
                    {showClose && (
                        <DialogActions>
                            <Button color="primary" onClick={() => updateUploadState(undefined)}>
                                <Clear className="iconMarginRight" />
                                Schließen
                            </Button>
                        </DialogActions>
                    )}
                </Dialog>
            )}
        </div>
    ) : (
        <div
            data-testid={!checkUndefinedNullOrEmpty(testid) ? testid : id}
            style={{ height: height || undefined, position: 'relative', width: !autoWidth ? '100%' : undefined }}
            key={'inputImage_' + id}
        >
            {type === 'avatar' ? (
                <Avatar key="avatar" src={convertImageValue(value, loading) || 'undefined'} style={{ width: height, height: height }} />
            ) : type === 'image360i' ? (
                elementSphere360
            ) : type === 'image360a' ? (
                elementSprite360
            ) : (
                <ImageEl key="imageElement" contain={!cover} externalImage={externalImage} onLoad={onLoad} ratio={aspectRatio} src={convertImageValue(value, loading)} onClick={onClick} />
            )}
            {showOverlay && (
                <Box sx={styles.btnOverlay}>
                    {overlay.map(
                        (btn, i) =>
                            btn &&
                            !btn.hidden && (
                                <Tooltip key={'imgOverlay_' + i} title={btn.title}>
                                    <span>
                                        <IconButton
                                            aria-label={btn.title}
                                            disabled={btn.disabled}
                                            onClick={btn.func}
                                            style={{ color: btn.disabled ? '#aaa' : '#fff', fontSize: '2rem', ...btn.style }}
                                            size="large"
                                        >
                                            {btn.textIcon ? btn.textIcon : <Iconify icon={btn.icon} style={{ color: 'inherit' }} />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )
                    )}
                </Box>
            )}{' '}
        </div>
    );
}

export default InputImage;
